import React, {useEffect, Suspense, lazy} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate, useLocation} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import NProgress from 'nprogress';
import {getUserToken, logout} from "./util/util";
import './index.css';
import Logo from '../src/assets/img/om-full-primary-logo.svg';
import secureLocalStorage from "react-secure-storage";
import { queryClient } from "./lib/queryClient";
import antdTheme from './theme/antdTheme';
import RouteFallback from './components/RouteFallback';

// Keep the React Query devtools out of the production bundle.
const ReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    ? require('@tanstack/react-query-devtools').ReactQueryDevtools
    : null;

// Route-level code splitting.
const AuthenticatedRoutes = lazy(() =>
  import('./layouts/AuthenticatedRoutes').then((m) => ({ default: m.AuthenticatedRoutes })),
);
const UnAuthenticatedRoutes = lazy(() =>
  import('./layouts/UnAuthenticatedRoutes').then((m) => ({ default: m.UnAuthenticatedRoutes })),
);
const UnAuthenticatedRoutesForgotPassword = lazy(() =>
  import('./layouts/UnAuthenticatedRoutesForgotPassword').then((m) => ({ default: m.UnAuthenticatedRoutesForgotPassword })),
);

const App = () => {

  return (
    <ConfigProvider theme={antdTheme}>
      <QueryClientProvider client={queryClient}>
        <Router>
            <ProgressBarHandler />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
                {/* Root Route - Redirect based on token */}
                <Route path="/" element={<RootRedirect />} />

                {/* Protected Routes - Wrapped with PrivateRoute */}
                <Route path="/dashboard/*" element={ <PrivateRoute><AuthenticatedRoutes /></PrivateRoute> } />

                {/* Public Routes */}
                <Route path="/onboard/*" element={<UnAuthenticatedRoutes />} />
                <Route path="/forgot-password/*" element={<UnAuthenticatedRoutesForgotPassword />} />

              {/* 404 Page */}
              <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Router>
        {process.env.NODE_ENV === 'development' && ReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ConfigProvider>
  );
}

// Redirect logic for the root path "/"
const RootRedirect = () => {
    const session = getUserToken();
    const expiresAt = secureLocalStorage.getItem('expiresAt');
    const isExpired = isTokenExpired(expiresAt);
    const mustChangePassword = secureLocalStorage.getItem('mustChangePassword') === true ||
        secureLocalStorage.getItem('mustChangePassword') === 'true';

    if (session && !isExpired) {
        return <Navigate to={mustChangePassword ? '/onboard/create-password' : '/dashboard'} replace />;
    } else {
        logout();
        return <Navigate to="/onboard/login" replace />;
    }
};

const isTokenExpired = (expiresAt) => {
    const currentTime = Date.now();
    const expiry = parseInt(expiresAt, 10);
    return expiry && expiry < currentTime;
};

// Component to handle protected routes
const PrivateRoute = ({ children }) => {
    const token = getUserToken();
    const expiresAt = secureLocalStorage.getItem('expiresAt');
    const isExpired = isTokenExpired(expiresAt);
    const mustChangePassword = secureLocalStorage.getItem('mustChangePassword') === true ||
        secureLocalStorage.getItem('mustChangePassword') === 'true';

    useEffect(() => {
        if (!token || isExpired) {
            logout();
            window.location.href = "/onboard/login"; // Force redirect if token missing or expired
        }
    }, [token, isExpired]);

    if (token && !isExpired && mustChangePassword) {
        return <Navigate to="/onboard/create-password" replace />;
    }

    return token && !isExpired ? children : null; // Prevent rendering protected content without token
};

// ProgressBarHandler Component to handle NProgress
const ProgressBarHandler = () => {
    const location = useLocation();

    useEffect(() => {
        NProgress.start();
        NProgress.done();
        return () => { NProgress.done(); };
    }, [location]);
    return null;
};

// 404 Not Found Page
const NotFound = () => (
    <div style={{ textAlign: "center", marginTop: "10%" }}>
        <img src={Logo} alt="OM Logo"  className='mb-5'/>
        <h1>404 - Page Not Found</h1>
        <a className="btn btn-primary mt-4" href='/'>Go to Home <i className="bi bi-house"/></a>
    </div>
);

export default App;
